import { Flex, Text } from '@chakra-ui/react'
import { RoutePaths } from 'containers/Core/Routes'
import { useBreakpointContext } from 'context/Breakpoint'
import { motion } from 'framer-motion'
import useAppointmentTypeId from 'hooks/useAppointments/useAppointmentTypeId'
import useElation from 'hooks/useElation'
import useUser from 'hooks/useUser/useUser'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import { BookingEventSource } from 'utils/amplitude.types'
import { generateAppointmentLink } from 'utils/appointments.utils'
import { PROGRAMS } from 'utils/program.utils'
import OnboardingContainer from '../OnboardingContainer'

const steps = [
  'Integrating your care needs',
  'Searching our providers',
  'Matching you to your preferred providers',
]

const MotionBox = motion.div

const ANIMATION_INITIAL_DELAY = 500
const ANIMATION_SHOW_DELAY = 1000
const ANIMATION_COMPLETE_DELAY = 500
const COLOR_TRANSITION_DURATION = ANIMATION_COMPLETE_DELAY / 1000 / 2

const StepItem = React.memo<{
  text: string
  isVisible: boolean
  isCompleted: boolean
  isMobile: boolean
}>(({ text, isVisible, isCompleted, isMobile }) => (
  <MotionBox
    initial={{ opacity: 0, x: 30 }}
    animate={isVisible ? { opacity: 1, x: 0 } : {}}
    transition={{ duration: COLOR_TRANSITION_DURATION }}
    style={{
      width: isMobile ? '90%' : '500px',
      backgroundColor: 'white',
      padding: '16px',
      borderRadius: '6px',
      boxShadow:
        '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      borderLeft: '4px solid',
      borderLeftColor: isCompleted ? '#3182CE' : '#e2e8f0',
      transition: `border-color ${COLOR_TRANSITION_DURATION}s ease-in-out`,
    }}
  >
    <Flex align="center" gap={3}>
      <motion.svg
        viewBox="0 0 24 24"
        fill="none"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: '20px', height: '20px' }}
      >
        {/* Circle */}
        <motion.circle
          cx="12"
          cy="12"
          r="11"
          stroke="#e2e8f0"
          fill="white"
          animate={isCompleted ? { stroke: '#3182CE' } : {}}
          transition={{
            duration: COLOR_TRANSITION_DURATION,
            ease: 'easeInOut',
          }}
        />
        {/* Check */}
        <polyline points="7,12 11,16 17,8" stroke="#FFF" strokeWidth="2" />
        <motion.polyline
          points="7,12 11,16 17,8"
          stroke="#3182CE"
          strokeWidth="2"
          initial={{
            strokeDasharray: '0 100',
            strokeDashoffset: 0,
            stroke: '#FFFFFF',
          }}
          animate={
            isCompleted
              ? {
                  strokeDasharray: '100 0',
                  strokeDashoffset: 0,
                  stroke: ['#FFFFFF', '#e2e8f0', '#3182CE'],
                }
              : {}
          }
          transition={{
            duration: COLOR_TRANSITION_DURATION,
            ease: 'easeInOut',
            stroke: {
              duration: COLOR_TRANSITION_DURATION,
              times: [0, 0.5, 1],
            },
          }}
        />
      </motion.svg>
      <Text
        color={isCompleted ? 'blue.600' : 'gray.500'}
        transition={`all ${COLOR_TRANSITION_DURATION}s ease-in-out`}
        fontWeight="medium"
        m={0}
      >
        {text}
      </Text>
    </Flex>
  </MotionBox>
))

StepItem.displayName = 'StepItem'

// Custom hook for managing the animation loop
const useAnimationLoop = () => {
  const [state, setState] = useState({
    activeStep: -1,
    completedSteps: [] as number[],
    loopCount: 0,
  })
  const timeoutRefs = useRef<NodeJS.Timeout[]>([])
  const isMounted = useRef(true)
  const loopTimeoutRef = useRef<NodeJS.Timeout>()

  const clearAllTimeouts = useCallback(() => {
    timeoutRefs.current.forEach(clearTimeout)
    timeoutRefs.current = []

    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current)
      loopTimeoutRef.current = undefined
    }
  }, [])

  const playLoop = useCallback(() => {
    if (!isMounted.current) {
      return
    }

    clearAllTimeouts()
    setState((prev) => ({ ...prev, activeStep: -1, completedSteps: [] }))
    const timeouts: NodeJS.Timeout[] = []

    steps.forEach((_, i) => {
      // Show step with initial delay
      const showTimeout = setTimeout(() => {
        if (isMounted.current) {
          setState((prev) => ({ ...prev, activeStep: i }))
        }
      }, ANIMATION_INITIAL_DELAY + i * ANIMATION_SHOW_DELAY)
      timeouts.push(showTimeout)

      // Complete step
      const completeTimeout = setTimeout(() => {
        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            completedSteps: [...prev.completedSteps, i],
          }))
        }
      }, ANIMATION_INITIAL_DELAY + i * ANIMATION_SHOW_DELAY + ANIMATION_COMPLETE_DELAY)
      timeouts.push(completeTimeout)
    })

    timeoutRefs.current = timeouts

    // When loop finishes, just restart it and increment loop count
    const totalDuration =
      ANIMATION_INITIAL_DELAY +
      steps.length * ANIMATION_SHOW_DELAY +
      ANIMATION_COMPLETE_DELAY
    loopTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) {
        playLoop()
      }

      setState((prev) => ({ ...prev, loopCount: prev.loopCount + 1 }))
    }, totalDuration)
  }, [clearAllTimeouts])

  useEffect(() => {
    isMounted.current = true
    playLoop()

    return () => {
      isMounted.current = false
      clearAllTimeouts()
    }
  }, [playLoop, clearAllTimeouts])

  return state
}

const SettingUpAccount: React.FC = () => {
  const { isMobile } = useBreakpointContext()
  const history = useHistory()
  const { appointmentTypeId } = useAppointmentTypeId('Initial MD')
  const {
    enableElationAccountCreationAtPayment,
    telehealthShouldBeACheckbox,
  } = useFlags()
  const { createPatient } = useUser()
  const [patientCreated, setPatientCreated] = useState(false)
  const hasRun = useRef(false)
  const { createElationAccount } = useElation()

  // Start animation
  const { activeStep, completedSteps, loopCount } = useAnimationLoop()

  const appointmentLink = useMemo(() => {
    if (!appointmentTypeId) {
      return ''
    }

    return generateAppointmentLink(appointmentTypeId, {
      source: BookingEventSource.INSURANCE_USER_ONBOARDING,
      patient_action_id: null,
      patient_action_definition: null,
      resource_id: null,
    })
  }, [appointmentTypeId])

  const handleComplete = () => {
    if (!appointmentLink) {
      return
    }

    history.push(appointmentLink)
  }

  useEffect(() => {
    if (hasRun.current) {
      return
    }

    hasRun.current = true

    const triggerCreatePatient = async () => {
      // Creates careOps account
      createPatient(PROGRAMS.INSURANCE).then(async (patient) => {
        if (patient) {
          if (!enableElationAccountCreationAtPayment) {
            // Creates Elation account will be required here
            createElationAccount().then(() => {
              setPatientCreated(true)
            })
          } else {
            setPatientCreated(true)
          }
        }
      })
    }

    triggerCreatePatient()
  }, [])

  useEffect(() => {
    // Handle completion when dependencies are ready
    if (appointmentTypeId && patientCreated && loopCount >= 1) {
      handleComplete()
    }
  }, [appointmentTypeId, patientCreated, loopCount, handleComplete])

  const stepsElements = useMemo(
    () =>
      steps.map((text, i) => {
        const isVisible = i <= activeStep
        const isCompleted = completedSteps.includes(i)

        return (
          <StepItem
            key={i}
            text={text}
            isVisible={isVisible}
            isCompleted={isCompleted}
            isMobile={isMobile}
          />
        )
      }),
    [activeStep, completedSteps, isMobile]
  )

  return (
    <>
      {telehealthShouldBeACheckbox ? (
        <OnboardingContainer>
          <Flex direction="column" gap={4} align="center">
            {stepsElements}
          </Flex>
        </OnboardingContainer>
      ) : (
        <Redirect to={RoutePaths.CONSENT_TO_TELEHEALTH} />
      )}
    </>
  )
}

export default SettingUpAccount
